import React, { useState, useEffect } from 'react';
import { adminAPI, balanceAPI, User, Property, getImageUrl } from '../api/api';
import { useLanguage } from '../contexts/LanguageContext';

interface Stats {
  total_users: number;
  total_properties: number;
  active_properties: number;
  pending_properties: number;
  new_users_today: number;
  new_properties_today: number;
  properties_last_week: number;
  users_last_week: number;
}

interface Category {
  id: number;
  name_ru: string;
  name_uz: string;
  slug: string;
  description_ru?: string;
  description_uz?: string;
  icon?: string;
  order: number;
  is_active: boolean;
}

const AdminPanelNew: React.FC = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState<'moderation' | 'users' | 'settings' | 'stats' | 'categories'>('moderation');
  const [stats, setStats] = useState<Stats | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [pendingProperties, setPendingProperties] = useState<Property[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  // Settings
  const [listingPrice, setListingPrice] = useState<number>(10000);
  const [newListingPrice, setNewListingPrice] = useState<string>('10000');
  const [savingSettings, setSavingSettings] = useState(false);

  // Category Modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name_ru: '',
    name_uz: '',
    slug: '',
    description_ru: '',
    description_uz: '',
    icon: '',
    order: 0,
    is_active: true,
  });

  // User Topup Modal
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [topupAmount, setTopupAmount] = useState('50000');
  const [topupLoading, setTopupLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'stats') {
        const res = await adminAPI.getStatistics();
        setStats(res.data);
      } else if (activeTab === 'moderation') {
        const res = await adminAPI.getPendingProperties();
        setPendingProperties(res.data);
      } else if (activeTab === 'users') {
        const res = await adminAPI.listUsers();
        setUsers(res.data);
      } else if (activeTab === 'settings') {
        const res = await adminAPI.getListingPrice();
        setListingPrice(res.data.listing_price ?? 10000);
        setNewListingPrice(String(res.data.listing_price ?? 10000));
      } else if (activeTab === 'categories') {
        const res = await adminAPI.listCategories();
        setCategories(res.data);
      }
    } catch (error) {
      console.error('Failed to load admin data', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveProperty = async (id: number) => {
    try {
      await adminAPI.approveProperty(id);
      loadData();
      alert(language === 'ru' ? 'Объявление одобрено!' : "E'lon tasdiqlandi!");
    } catch (error) {
      console.error('Failed to approve property', error);
      alert('Ошибка при одобрении');
    }
  };

  const handleRejectProperty = async (id: number) => {
    const reason = prompt(language === 'ru' ? 'Причина отклонения:' : 'Rad etish sababi:');
    if (!reason) return;

    try {
      await adminAPI.rejectProperty(id, reason);
      loadData();
      alert(language === 'ru' ? 'Объявление отклонено!' : "E'lon rad etildi!");
    } catch (error) {
      console.error('Failed to reject property', error);
      alert('Ошибка при отклонении');
    }
  };

  const handleAdminTopup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      setTopupLoading(true);
      await balanceAPI.adminTopup(selectedUser.id, Number(topupAmount));
      alert(
        language === 'ru'
          ? `Баланс пользователя успешно пополнен на ${Number(topupAmount).toLocaleString()} сум!`
          : `Foydalanuvchi balansi ${Number(topupAmount).toLocaleString()} so'mga to'ldirildi!`
      );
      setShowTopupModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      console.error('Failed to topup user', error);
      alert('Ошибка при пополнении баланса');
    } finally {
      setTopupLoading(false);
    }
  };

  const handleSaveListingPrice = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSavingSettings(true);
      const priceNum = Number(newListingPrice);
      await adminAPI.setListingPrice(priceNum);
      setListingPrice(priceNum);
      alert(
        language === 'ru'
          ? `Стоимость размещения успешно установлена: ${priceNum === 0 ? 'Бесплатно' : `${priceNum.toLocaleString()} сум`}!`
          : `E'lon joylashtirish narxi o'rnatildi: ${priceNum === 0 ? 'Bepul' : `${priceNum.toLocaleString()} so'm`}!`
      );
    } catch (error) {
      console.error('Failed to set listing price', error);
      alert('Xatolik yuz berdi');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await adminAPI.updateCategory(editingCategory.id, categoryForm);
      } else {
        await adminAPI.createCategory(categoryForm);
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({
        name_ru: '',
        name_uz: '',
        slug: '',
        description_ru: '',
        description_uz: '',
        icon: '',
        order: 0,
        is_active: true,
      });
      loadData();
    } catch (error) {
      console.error('Failed to save category', error);
    }
  };

  const handleEditCategory = (cat: Category) => {
    setEditingCategory(cat);
    setCategoryForm({
      name_ru: cat.name_ru,
      name_uz: cat.name_uz,
      slug: cat.slug,
      description_ru: cat.description_ru || '',
      description_uz: cat.description_uz || '',
      icon: cat.icon || '',
      order: cat.order,
      is_active: cat.is_active,
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (id: number) => {
    if (window.confirm(language === 'ru' ? 'Удалить категорию?' : "Kategoriyani o'chirish?")) {
      try {
        await adminAPI.deleteCategory(id);
        loadData();
      } catch (error) {
        console.error('Failed to delete category', error);
      }
    }
  };

  const StatCard: React.FC<{ title: string; value: number; icon: string; color: string }> = ({ title, value, icon, color }) => (
    <div className={`card p-6 border-l-4 ${color}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm mb-1">{title}</p>
          <p className="text-3xl font-bold">{value.toLocaleString()}</p>
        </div>
        <div className="text-4xl opacity-20">{icon}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-6">{t('adminPanel')}</h1>

        {/* Navigation Tabs */}
        <div className="flex overflow-x-auto border-b mb-6 -mx-4 px-4 sm:mx-0 sm:px-0 gap-2 scrollbar-none">
          {[
            { key: 'moderation', label: `${t('moderation')} (${pendingProperties.length})`, icon: '🛡️' },
            { key: 'users', label: t('users'), icon: '👥' },
            { key: 'settings', label: t('settingsTab'), icon: '⚙️' },
            { key: 'stats', label: t('statistics'), icon: '📊' },
            { key: 'categories', label: t('categories'), icon: '📁' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`px-3.5 sm:px-4 py-2.5 sm:py-3 font-semibold text-xs sm:text-sm whitespace-nowrap flex items-center space-x-2 rounded-t-xl transition ${
                activeTab === tab.key
                  ? 'text-primary-600 border-b-2 border-primary-600 bg-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 bg-gray-100'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {/* Moderation Tab */}
            {activeTab === 'moderation' && (
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-5 text-gray-900">
                  {language === 'ru' ? 'Объявления на проверке' : "Tekshiruvdagi e'lonlar"} ({pendingProperties.length})
                </h2>
                {pendingProperties.length === 0 ? (
                  <div className="card p-8 sm:p-12 text-center">
                    <div className="text-5xl mb-3">✅</div>
                    <p className="text-gray-500 text-base font-semibold">
                      {language === 'ru' ? 'Нет объявлений на проверке' : "Tekshiruv uchun e'lonlar mavjud emas"}
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-6">
                    {pendingProperties.map((property) => (
                      <div key={property.id} className="card p-4 sm:p-6 hover:shadow-lg transition">
                        <div className="flex flex-col md:flex-row gap-5">
                          {/* Photos gallery */}
                          <div className="w-full md:w-64 flex-shrink-0">
                            {property.images && property.images.length > 0 ? (
                              <div className="space-y-2">
                                <img
                                  src={getImageUrl(property.images[0].image_url)}
                                  alt={property.title}
                                  className="w-full h-44 object-cover rounded-xl border"
                                />
                                {property.images.length > 1 && (
                                  <div className="flex gap-1.5 overflow-x-auto pb-1">
                                    {property.images.slice(1).map((img, i) => (
                                      <img
                                        key={i}
                                        src={getImageUrl(img.image_url)}
                                        alt=""
                                        className="w-14 h-14 object-cover rounded-lg border flex-shrink-0"
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="w-full h-44 bg-gray-100 rounded-xl flex items-center justify-center text-4xl">
                                🏠
                              </div>
                            )}
                          </div>

                          {/* Property Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                                {property.title}
                              </h3>
                              <span className="px-2.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full flex-shrink-0">
                                ⏳ {t('pending')}
                              </span>
                            </div>

                            <p className="text-2xl font-black text-primary-600 mb-2.5">
                              {Number(property.price).toLocaleString()} <span className="text-base font-bold text-gray-500">{t('sum')}</span>
                            </p>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs sm:text-sm text-gray-600 mb-3 bg-gray-50 p-3 rounded-xl border border-gray-100">
                              <div><strong>{t('city')}:</strong> {property.city}</div>
                              <div><strong>{t('area')}:</strong> {property.area} m²</div>
                              {property.rooms && <div><strong>{t('rooms')}:</strong> {property.rooms}</div>}
                              {property.address && <div className="col-span-2"><strong>{t('address')}:</strong> {property.address}</div>}
                              <div><strong>ID:</strong> #{property.id}</div>
                              <div><strong>Owner ID:</strong> #{property.owner_id}</div>
                            </div>

                            {property.description && (
                              <p className="text-xs sm:text-sm text-gray-700 bg-white p-3 border rounded-xl mb-4 whitespace-pre-line">
                                {property.description}
                              </p>
                            )}

                            <div className="flex flex-wrap gap-3 items-center justify-between pt-2 border-t">
                              <span className="text-xs text-gray-400">
                                {t('created')}: {new Date(property.created_at).toLocaleString()}
                              </span>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleApproveProperty(property.id)}
                                  className="btn bg-green-600 text-white hover:bg-green-700 text-xs sm:text-sm font-bold px-4 py-2"
                                >
                                  ✅ {t('approve')}
                                </button>
                                <button
                                  onClick={() => handleRejectProperty(property.id)}
                                  className="btn bg-red-600 text-white hover:bg-red-700 text-xs sm:text-sm font-bold px-4 py-2"
                                >
                                  ❌ {t('reject')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-5 text-gray-900">
                  {t('users')} ({users.length})
                </h2>
                <div className="card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 border-b text-gray-600 uppercase text-xs">
                        <tr>
                          <th className="p-3 sm:p-4">ID</th>
                          <th className="p-3 sm:p-4">{language === 'ru' ? 'Пользователь' : 'Foydalanuvchi'}</th>
                          <th className="p-3 sm:p-4">{t('sellerPhone')}</th>
                          <th className="p-3 sm:p-4">{t('yourBalance')}</th>
                          <th className="p-3 sm:p-4">{t('status')}</th>
                          <th className="p-3 sm:p-4">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {users.map((u) => (
                          <tr key={u.id} className="hover:bg-gray-50 transition">
                            <td className="p-3 sm:p-4 font-mono font-bold text-gray-500">#{u.id}</td>
                            <td className="p-3 sm:p-4">
                              <div className="font-semibold text-gray-900">{u.first_name} {u.last_name}</div>
                              <div className="text-xs text-gray-400">@{u.username || u.telegram_id}</div>
                            </td>
                            <td className="p-3 sm:p-4 font-mono text-gray-700">{u.phone || '—'}</td>
                            <td className="p-3 sm:p-4">
                              <span className="font-bold text-primary-700 bg-primary-50 px-2.5 py-1 rounded-lg border border-primary-100">
                                {(u.balance || 0).toLocaleString()} {t('sum')}
                              </span>
                            </td>
                            <td className="p-3 sm:p-4">
                              {u.is_admin ? (
                                <span className="bg-purple-100 text-purple-800 font-bold px-2 py-0.5 rounded text-xs">Admin</span>
                              ) : (
                                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">User</span>
                              )}
                            </td>
                            <td className="p-3 sm:p-4">
                              <button
                                onClick={() => {
                                  setSelectedUser(u);
                                  setShowTopupModal(true);
                                }}
                                className="btn bg-primary-50 text-primary-700 hover:bg-primary-100 text-xs font-bold py-1.5 px-3 rounded-lg border border-primary-200"
                              >
                                💰 {t('topup')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Tab (Listing Price) */}
            {activeTab === 'settings' && (
              <div className="max-w-xl">
                <h2 className="text-xl sm:text-2xl font-bold mb-5 text-gray-900">
                  ⚙️ {t('settingsTab')}
                </h2>

                <div className="card p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {t('listingPriceSettingTitle')}
                  </h3>
                  <p className="text-gray-500 text-xs sm:text-sm mb-4">
                    {language === 'ru'
                      ? 'Укажите сумму, которая автоматически списывается с баланса пользователя при подаче объявления. Укажите 0 для бесплатного размещения.'
                      : "Foydalanuvchi e'lon berayotganda balansidan avtomatik yechiladigan summani belgilang. Bepul e'lonlar uchun 0 kiriting."}
                  </p>

                  <form onSubmit={handleSaveListingPrice} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        {language === 'ru' ? 'Стоимость в сумах (UZS)' : "Narxi so'mda (UZS)"}
                      </label>
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        {[0, 5000, 10000, 20000].map((p) => (
                          <button
                            type="button"
                            key={p}
                            onClick={() => setNewListingPrice(String(p))}
                            className={`p-2 rounded-xl text-xs font-bold border transition ${
                              newListingPrice === String(p)
                                ? 'border-primary-600 bg-primary-50 text-primary-700'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {p === 0 ? (language === 'ru' ? '0 (Бесплатно)' : '0 (Bepul)') : `${p.toLocaleString()} so'm`}
                          </button>
                        ))}
                      </div>
                      <input
                        type="number"
                        className="input"
                        value={newListingPrice}
                        onChange={(e) => setNewListingPrice(e.target.value)}
                        min="0"
                        step="1000"
                        required
                      />
                    </div>

                    <div className="bg-gray-50 p-3 rounded-xl text-xs text-gray-600">
                      <strong>Hozirgi narx:</strong> {listingPrice === 0 ? '0 (Bepul)' : `${listingPrice.toLocaleString()} so'm`}
                    </div>

                    <button
                      type="submit"
                      disabled={savingSettings}
                      className="btn btn-primary w-full py-3 font-bold text-sm shadow"
                    >
                      {savingSettings ? t('loading') : t('save')}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Statistics Tab */}
            {activeTab === 'stats' && stats && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                  <StatCard title={t('totalUsers')} value={stats.total_users} icon="👥" color="border-blue-500" />
                  <StatCard title={t('totalProperties')} value={stats.total_properties} icon="🏠" color="border-green-500" />
                  <StatCard title={t('activeProperties')} value={stats.active_properties} icon="✅" color="border-purple-500" />
                  <StatCard title={t('pendingProperties')} value={stats.pending_properties} icon="⏳" color="border-yellow-500" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="card p-6">
                    <h3 className="text-lg font-semibold mb-4">📈 {t('newUsersToday')}</h3>
                    <p className="text-4xl font-bold text-primary-600">{stats.new_users_today}</p>
                    <p className="text-sm text-gray-500 mt-2">За последние 7 дней: {stats.users_last_week}</p>
                  </div>
                  <div className="card p-6">
                    <h3 className="text-lg font-semibold mb-4">🏡 {t('newPropertiesToday')}</h3>
                    <p className="text-4xl font-bold text-primary-600">{stats.new_properties_today}</p>
                    <p className="text-sm text-gray-500 mt-2">За последние 7 дней: {stats.properties_last_week}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold">{t('categories')}</h2>
                  <button onClick={() => setShowCategoryModal(true)} className="btn btn-primary w-full sm:w-auto">
                    + {t('addCategory')}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {categories.map((cat) => (
                    <div key={cat.id} className="card p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div className="text-3xl">{cat.icon || '📁'}</div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${cat.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {cat.is_active ? 'Активна' : 'Неактивна'}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold mb-1">{language === 'ru' ? cat.name_ru : cat.name_uz}</h3>
                      <p className="text-sm text-gray-600 mb-1">Slug: {cat.slug}</p>
                      <p className="text-sm text-gray-500 mb-4">Порядок: {cat.order}</p>
                      <div className="flex gap-2">
                        <button onClick={() => handleEditCategory(cat)} className="btn btn-secondary text-sm flex-1">
                          {t('edit')}
                        </button>
                        <button onClick={() => handleDeleteCategory(cat.id)} className="btn bg-red-100 text-red-700 hover:bg-red-200 text-sm">
                          {t('delete')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Topup User Balance Modal */}
        {showTopupModal && selectedUser && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
              <div className="flex justify-between items-center mb-4 pb-2 border-b">
                <h3 className="text-lg font-bold text-gray-900">
                  {language === 'ru' ? 'Пополнить баланс пользователя' : "Foydalanuvchi balansini to'ldirish"}
                </h3>
                <button onClick={() => { setShowTopupModal(false); setSelectedUser(null); }} className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 font-bold">×</button>
              </div>

              <div className="bg-gray-50 p-3.5 rounded-xl mb-4 text-xs sm:text-sm">
                <p><strong>Foydalanuvchi:</strong> {selectedUser.first_name} {selectedUser.last_name} (@{selectedUser.username || selectedUser.telegram_id})</p>
                <p><strong>Telefon:</strong> {selectedUser.phone || '—'}</p>
                <p><strong>Hozirgi balans:</strong> {(selectedUser.balance || 0).toLocaleString()} {t('sum')}</p>
              </div>

              <form onSubmit={handleAdminTopup} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    {language === 'ru' ? 'Сумма пополнения (сум)' : "To'ldirish miqdori (so'm)"}
                  </label>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[10000, 25000, 50000, 100000, 200000, 500000].map((amt) => (
                      <button
                        type="button"
                        key={amt}
                        onClick={() => setTopupAmount(String(amt))}
                        className={`p-2 rounded-xl text-xs font-bold border transition ${
                          topupAmount === String(amt) ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200'
                        }`}
                      >
                        {amt.toLocaleString()}
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    className="input text-sm"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    required
                  />
                </div>

                <div className="flex gap-2.5 pt-2">
                  <button type="submit" disabled={topupLoading} className="btn btn-primary flex-1 font-bold py-3 text-sm">
                    {topupLoading ? t('loading') : `+ ${Number(topupAmount).toLocaleString()} ${t('sum')}`}
                  </button>
                  <button type="button" onClick={() => { setShowTopupModal(false); setSelectedUser(null); }} className="btn btn-secondary font-bold py-3 text-sm">
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Category Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
              <div className="flex justify-between items-center mb-6 pb-2 border-b">
                <h2 className="text-xl font-bold">
                  {editingCategory ? 'Редактировать категорию' : t('addCategory')}
                </h2>
                <button onClick={() => setShowCategoryModal(false)} className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 font-bold">
                  ×
                </button>
              </div>

              <form onSubmit={handleCategorySubmit} className="space-y-4 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Название (RU)</label>
                    <input
                      type="text"
                      className="input"
                      value={categoryForm.name_ru}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name_ru: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Название (UZ)</label>
                    <input
                      type="text"
                      className="input"
                      value={categoryForm.name_uz}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name_uz: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Slug</label>
                    <input
                      type="text"
                      className="input"
                      value={categoryForm.slug}
                      onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Иконка (emoji)</label>
                    <input
                      type="text"
                      className="input"
                      value={categoryForm.icon}
                      onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Описание (RU)</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={categoryForm.description_ru}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description_ru: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Описание (UZ)</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={categoryForm.description_uz}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description_uz: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Порядок</label>
                    <input
                      type="number"
                      className="input"
                      value={categoryForm.order}
                      onChange={(e) => setCategoryForm({ ...categoryForm, order: Number(e.target.value) })}
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={categoryForm.is_active}
                        onChange={(e) => setCategoryForm({ ...categoryForm, is_active: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-xs font-bold text-gray-700">Активна</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="btn btn-primary flex-1 font-bold py-3">
                    {t('save')}
                  </button>
                  <button type="button" onClick={() => setShowCategoryModal(false)} className="btn btn-secondary font-bold py-3">
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanelNew;
